import { useCallback, useEffect, useState } from 'react';

/**
 * The customer card's add-to-home-screen prompt — features/CUSTOMER-CARD.md.
 *
 * ## Why this is a shortcut and not an install
 *
 * features/CUSTOMER-CARD.md asks for an add-to-home-screen prompt on the customer card, and AGENTS.md
 * §7 says `/staff/*` is the only installable surface. Both are right, and they are
 * not actually in conflict once the two mechanisms are separated:
 *
 *  - **Install** (`beforeinstallprompt`) needs a manifest and a service worker.
 *    Giving the card those would put a customer's card behind an app icon that
 *    caches its own shell — and a cached balance shown as current is the bug
 *    AGENTS.md §4.5 exists to prevent. It would also pull the counter's
 *    precache onto a customer's phone, over their mobile data.
 *  - **Shortcut** is what every mobile browser offers from its own menu for an
 *    ordinary page: a home-screen icon that opens the URL in the browser, live
 *    every time. That is exactly what a card needs, and it needs no manifest
 *    at all.
 *
 * So the app cannot *call* an install prompt here, and should not. What it can
 * do is tell the customer where the browser's own item is, once, at the moment
 * they have a card worth keeping. The conflict and this resolution are recorded
 * in ARCHITECTURE.md §7.
 *
 * ## Why it is worth showing at all
 *
 * A card reached by typing a phone number and waiting for an OTP is a card
 * nobody opens twice. The icon is the difference between the card being present
 * at the counter and the barista falling back to a phone lookup, which is the
 * slow path in the flow features/STAFF-COUNTER.md is timed against.
 */

const DISMISSED_KEY = 'signapass.a2hs.dismissed';

/** Long enough that a re-prompt reads as a reminder, not as nagging. */
const REPROMPT_AFTER_MS = 30 * 24 * 60 * 60_000;

export type AddToHomeScreenPlatform = 'ios' | 'android' | 'other';

/**
 * Which set of instructions to show.
 *
 * User-agent sniffing, deliberately. There is no feature to detect: the item
 * lives in browser chrome the page cannot see, and its name and location differ
 * per platform. Getting it wrong costs a customer one confusing sentence, which
 * is why `other` exists and says something generic rather than guessing.
 */
export function detectPlatform(userAgent: string = navigator.userAgent): AddToHomeScreenPlatform {
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what separates
  // an iPad from a desktop Safari that has no home screen to add to.
  const isIpadOS = /Macintosh/.test(userAgent) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(userAgent) || isIpadOS) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  return 'other';
}

/**
 * Already opened from a home-screen icon — there is nothing left to suggest.
 *
 * Both signals are optional-chained. Every browser that can add to a home
 * screen has `matchMedia`, but this runs during render on a surface a customer
 * reaches from a link, and a tip is not worth a blank card if some embedded
 * webview turns out not to.
 */
export function isStandalone(): boolean {
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // Safari's own, predating the media query and still the only signal on iOS.
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function dismissedRecently(now: number): boolean {
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const at = Number(raw);
    // A corrupt value should not pin the prompt open forever, nor hide it
    // forever. Rewriting it as "dismissed just now" is what actually makes the
    // timer restart — returning true on its own would hide the tip for good,
    // because the unreadable value stays unreadable on every later visit.
    if (!Number.isFinite(at)) {
      window.localStorage.setItem(DISMISSED_KEY, String(now));
      return true;
    }
    return now - at < REPROMPT_AFTER_MS;
  } catch {
    // Private browsing, or storage disabled. Showing the tip to someone who
    // already dismissed it is a smaller cost than never showing it at all.
    return false;
  }
}

export interface AddToHomeScreenTip {
  platform: AddToHomeScreenPlatform;
  visible: boolean;
  dismiss: () => void;
}

/**
 * Whether to show the tip, and how to put it away.
 *
 * `enabled` is the caller's business rule — MyCards passes "the customer has at
 * least one card". Suggesting a home-screen icon for an empty wallet is asking
 * someone to bookmark a page that says they have nothing.
 */
export function useAddToHomeScreenTip(enabled: boolean): AddToHomeScreenTip {
  const [dismissed, setDismissed] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [platform, setPlatform] = useState<AddToHomeScreenPlatform>('other');

  // In an effect rather than in render: both `matchMedia` and `localStorage`
  // are browser-only, and this component renders under jsdom in tests.
  useEffect(() => {
    setPlatform(detectPlatform());
    setEligible(!isStandalone() && !dismissedRecently(Date.now()));
  }, []);

  const dismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // Dismissal not persisting is a nuisance, not a failure. The tip is gone
      // for this session either way.
    }
  }, []);

  return {
    platform,
    // `other` is desktop in practice, where there is no home screen worth the
    // interruption.
    visible: enabled && eligible && !dismissed && platform !== 'other',
    dismiss,
  };
}
